// Goal-Driven Roadmap
// OKR-style grid using AG Grid

import { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
    AllCommunityModule,
    ModuleRegistry,
    ColDef,
    CellValueChangedEvent,
    ICellRendererParams,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useRoadmap } from '../shared/RoadmapContext';
import { gdApi } from '../shared/api';

import type { Quarter } from '../shared/types';
import { toast } from 'sonner';
import { LoadingState } from '@/components/ds';

// Register AG Grid modules.
// We use AllCommunityModule to pull in ClientSideRowModel + every community filter,
// editor, and validation module. Without this the grid emits errors #239 (missing
// ValidationModule) and #200 (ColumnFilter, NumberEditor) on first render. See
// tests/integration/goal-driven-modules.test.tsx (audit finding B3).
ModuleRegistry.registerModules([AllCommunityModule]);

// Helper: Progress bar renderer
// Thresholds per OKR standard: <40 = at risk (red), 40-70 = progressing (amber), >70 = on track (green)
const ProgressRenderer = (params: ICellRendererParams) => {
    if (params.data?.type === 'Initiative') return null;
    const progress = params.value || 0;
    let color = 'bg-green-500';
    if (progress < 40) color = 'bg-red-500';
    else if (progress < 70) color = 'bg-amber-500';

    return (
        <div className="flex items-center h-full w-full pr-4">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                    className={`h-full ${color}`}
                    style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                />
            </div>
            <span className="ml-2 text-xs font-mono w-8">{progress}%</span>
        </div>
    );
};

// Helper: Row type badge for Objective/Initiative column
const TypeRenderer = (params: ICellRendererParams) => {
    const type: string = params.data?.type;
    const isInitiative = type === 'Initiative';
    return (
        <div className="flex items-center h-full gap-2" style={{ paddingLeft: isInitiative ? '20px' : '0' }}>
            <span className={`px-1 rounded text-xs font-bold uppercase tracking-wider ${isInitiative ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                {isInitiative ? 'Init' : 'Obj'}
            </span>
            <span className={isInitiative ? 'text-sm' : 'font-bold'}>{params.value}</span>
        </div>
    );
};

export function GoalDrivenRoadmap() {
    const { user } = useAuth();
    const { roadmap } = useRoadmap();
    const [rowData, setRowData] = useState<unknown[]>([]);
    const [loading, setLoading] = useState(true);

    // Add Objective Dialog
    const [isAddingObjective, setIsAddingObjective] = useState(false);
    const [newObjectiveData, setNewObjectiveData] = useState<{
        title: string;
        keyResult: string;
        targetQuarter: Quarter;
        targetYear: string;
    }>({
        title: '',
        keyResult: '',
        targetQuarter: 'Q1',
        targetYear: new Date().getFullYear().toString(),
    });

    // GD-5: Add Initiative state
    const [isAddingInitiative, setIsAddingInitiative] = useState(false);
    const [newInitiativeData, setNewInitiativeData] = useState<{ title: string; objectiveId: string }>({
        title: '',
        objectiveId: '',
    });

    // GD-6: Delete state
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; type: 'Objective' | 'Initiative'; title: string } | null>(null);

    // GD-7: Edit Objective state
    const [editingObjective, setEditingObjective] = useState<{
        id: string;
        title: string;
        keyResult: string;
        targetQuarter: Quarter;
        targetYear: string;
    } | null>(null);

    // Load data
    const loadData = useCallback(async () => {
        if (!user || !roadmap) return;
        setLoading(true);
        try {
            const objectivesRes = await gdApi.listObjectives(roadmap.id);
            if (objectivesRes.data) {
                const rows: unknown[] = [];
                for (const obj of objectivesRes.data) {
                    rows.push({
                        id: obj.id,
                        type: 'Objective',
                        title: obj.title,
                        key_result: obj.key_result,
                        progress: obj.progress,
                        quarter: `${obj.target_quarter || '-'} ${obj.target_year || '-'}`,
                        status: '',
                        owner: '',
                    });
                    // Fetch and flatten initiatives under each objective (GD-1)
                    const { initiatives } = await gdApi.getObjectiveWithInitiatives(obj.id);
                    for (const init of initiatives?.data || []) {
                        // GD-11: include status, owner, start_date, end_date
                        rows.push({
                            id: init.id,
                            type: 'Initiative',
                            title: init.title,
                            key_result: '',
                            progress: null,
                            quarter: init.start_date ? `${init.start_date} → ${init.end_date || '?'}` : '',
                            objective_id: obj.id,
                            status: init.status,
                            owner: init.owner || '',
                        });
                    }
                }
                setRowData(rows);
            }
        } catch (error) {
            console.error('Failed to load goal driven roadmap:', error);
            toast.error('Failed to load goal driven roadmap');
        } finally {
            setLoading(false);
        }
    }, [user, roadmap]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleAddObjective = async () => {
        if (!newObjectiveData.title.trim() || !user || !roadmap) return;

        const { error } = await gdApi.createObjective({
            owner_id: user.id,
            roadmap_id: roadmap.id,
            title: newObjectiveData.title,
            key_result: newObjectiveData.keyResult,
            target_quarter: newObjectiveData.targetQuarter,
            target_year: parseInt(newObjectiveData.targetYear)
        });

        if (error) {
            toast.error('Failed to create objective');
            return;
        }

        // GD-8: Reset newObjectiveData after submission
        setNewObjectiveData({
            title: '',
            keyResult: '',
            targetQuarter: 'Q1',
            targetYear: new Date().getFullYear().toString(),
        });
        setIsAddingObjective(false);
        loadData();
        toast.success('Objective created');
    };

    // GD-5: Handle Add Initiative
    const handleAddInitiative = async () => {
        if (!newInitiativeData.title.trim() || !newInitiativeData.objectiveId) return;

        const { error } = await gdApi.createInitiative({
            objective_id: newInitiativeData.objectiveId,
            title: newInitiativeData.title,
        });

        if (error) {
            toast.error('Failed to create initiative');
            return;
        }

        setNewInitiativeData({ title: '', objectiveId: '' });
        setIsAddingInitiative(false);
        loadData();
        toast.success('Initiative created');
    };

    // GD-6: Handle Delete
    const handleDelete = async () => {
        if (!deleteTarget) return;

        let error: unknown = null;
        if (deleteTarget.type === 'Objective') {
            const res = await gdApi.deleteObjective(deleteTarget.id);
            error = res.error;
        } else {
            const res = await gdApi.deleteInitiative(deleteTarget.id);
            error = res.error;
        }

        if (error) {
            toast.error(`Failed to delete ${deleteTarget.type.toLowerCase()}`);
        } else {
            toast.success(`${deleteTarget.type} deleted`);
            loadData();
        }
        setDeleteTarget(null);
    };

    // GD-7: Handle Save Edit Objective
    const handleSaveEditObjective = async () => {
        if (!editingObjective || !editingObjective.title.trim()) return;

        const { error } = await gdApi.updateObjective(editingObjective.id, {
            title: editingObjective.title,
            key_result: editingObjective.keyResult,
            target_quarter: editingObjective.targetQuarter,
            target_year: parseInt(editingObjective.targetYear),
        });

        if (error) {
            toast.error('Failed to update objective');
            return;
        }

        toast.success('Objective updated');
        setEditingObjective(null);
        loadData();
    };

    // Derive list of objectives for the initiative selector
    const objectiveRows = useMemo(() => {
        return (rowData as Array<{ id: string; type: string; title: string }>).filter(r => r.type === 'Objective');
    }, [rowData]);

    const colDefs = useMemo<ColDef[]>(() => [
        { field: 'title', headerName: 'Objective / Initiative', flex: 2, cellRenderer: TypeRenderer },
        { field: 'key_result', headerName: 'Key Result', flex: 2 },
        { field: 'quarter', headerName: 'Timeline', width: 120 },
        {
            field: 'progress',
            headerName: 'Progress',
            cellRenderer: ProgressRenderer,
            flex: 1,
            // GD-12: Fix editable callback to return boolean
            editable: (params) => params.data?.type === 'Objective',
        },
        // GD-11: Status column
        {
            field: 'status',
            headerName: 'Status',
            width: 130,
            cellRenderer: (params: ICellRendererParams) => {
                if (params.data?.type !== 'Initiative' || !params.value) return null;
                // Mirrors STATUS_COLORS in shared/constants.ts — cancelled deepened
                // for legend distinctness vs planned/slate-400 (audit M6).
                const colors: Record<string, string> = {
                    planned: '#94a3b8',
                    in_progress: '#3b82f6',
                    completed: '#22c55e',
                    blocked: '#ef4444',
                    cancelled: '#1f2937',
                    deferred: '#f59e0b',
                };
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[params.value] || '#94a3b8' }} />
                        <span style={{ textTransform: 'capitalize', fontSize: 12 }}>{String(params.value).replace('_', ' ')}</span>
                    </div>
                );
            },
        },
        // GD-11: Owner column
        {
            field: 'owner',
            headerName: 'Owner',
            width: 120,
            valueGetter: (p: ICellRendererParams) => p.data?.type === 'Initiative' ? p.data.owner : '',
        },
        // GD-7: Edit column (Objectives only)
        {
            headerName: 'Edit',
            width: 70,
            sortable: false,
            filter: false,
            cellRenderer: (params: ICellRendererParams) => {
                if (params.data?.type !== 'Objective') return null;
                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Edit Objective"
                        onClick={() => setEditingObjective({
                            id: params.data.id,
                            title: params.data.title,
                            keyResult: params.data.key_result || '',
                            targetQuarter: (params.data.quarter?.split(' ')?.[0] as Quarter) || 'Q1',
                            targetYear: params.data.quarter?.split(' ')?.[1] || new Date().getFullYear().toString(),
                        })}
                    >
                        <Edit2 className="h-4 w-4" />
                    </Button>
                );
            },
        },
        // GD-6: Delete column
        {
            headerName: 'Delete',
            width: 80,
            sortable: false,
            filter: false,
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Delete ${params.data.type || 'Item'}`}
                        onClick={() => setDeleteTarget({ id: params.data.id, type: params.data.type, title: params.data.title })}
                    >
                        <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                );
            },
        },
    ], []);

    // Handle cell edits (e.g. progress update)
    const onCellValueChanged = async (event: CellValueChangedEvent) => {
        if (event.colDef.field === 'progress') {
            await gdApi.updateProgress(event.data.id, Number(event.newValue));
            toast.success('Progress updated');
        }
    };

    if (loading) return <LoadingState label="Loading OKRs..." />;

    return (
        <div className="p-6 space-y-6 h-full flex flex-col">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Goal Driven Roadmap (OKRs)</h2>
                <div className="flex items-center gap-2">
                    {/* GD-5: Add Initiative Button */}
                    <Button variant="outline" onClick={() => setIsAddingInitiative(true)}>
                        <Plus className="mr-2 h-4 w-4" /> Add Initiative
                    </Button>

                    {/* Add Objective Dialog */}
                    <Dialog open={isAddingObjective} onOpenChange={setIsAddingObjective}>
                        <DialogTrigger asChild>
                            <Button><Plus className="mr-2 h-4 w-4" /> Add Objective</Button>
                        </DialogTrigger>
                        <DialogContent aria-describedby={undefined}>
                            <DialogHeader><DialogTitle>New Objective</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="obj-title">Objective Title</Label>
                                    <Input
                                        id="obj-title"
                                        placeholder="e.g. Increase Market Share"
                                        value={newObjectiveData.title}
                                        onChange={e => setNewObjectiveData({ ...newObjectiveData, title: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="obj-kr">Key Result</Label>
                                    <Input
                                        id="obj-kr"
                                        placeholder="e.g. 50% growth"
                                        value={newObjectiveData.keyResult}
                                        onChange={e => setNewObjectiveData({ ...newObjectiveData, keyResult: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Quarter</Label>
                                        <Select
                                            value={newObjectiveData.targetQuarter}
                                            onValueChange={(v) => setNewObjectiveData({ ...newObjectiveData, targetQuarter: v as Quarter })}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Q1">Q1</SelectItem>
                                                <SelectItem value="Q2">Q2</SelectItem>
                                                <SelectItem value="Q3">Q3</SelectItem>
                                                <SelectItem value="Q4">Q4</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Year</Label>
                                        <Input
                                            type="number"
                                            value={newObjectiveData.targetYear}
                                            onChange={e => setNewObjectiveData({ ...newObjectiveData, targetYear: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* GD-10: DialogFooter with Cancel + Create buttons */}
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsAddingObjective(false)}>Cancel</Button>
                                <Button onClick={handleAddObjective} disabled={!newObjectiveData.title.trim()}>
                                    Create Objective
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* GD-9: autoHeight — remove fixed height, add domLayout */}
            <div className="ag-theme-quartz" style={{ width: '100%' }}>
                <AgGridReact
                    rowData={rowData}
                    columnDefs={colDefs}
                    onCellValueChanged={onCellValueChanged}
                    domLayout="autoHeight"
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                    }}
                />
            </div>

            {/* GD-5: Add Initiative Dialog */}
            <Dialog open={isAddingInitiative} onOpenChange={setIsAddingInitiative}>
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader><DialogTitle>New Initiative</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="init-title">Initiative Title</Label>
                            <Input
                                id="init-title"
                                placeholder="e.g. Launch beta program"
                                value={newInitiativeData.title}
                                onChange={e => setNewInitiativeData({ ...newInitiativeData, title: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Objective</Label>
                            <Select
                                value={newInitiativeData.objectiveId}
                                onValueChange={(v) => setNewInitiativeData({ ...newInitiativeData, objectiveId: v })}
                            >
                                <SelectTrigger><SelectValue placeholder="Select an objective" /></SelectTrigger>
                                <SelectContent>
                                    {objectiveRows.map(obj => (
                                        <SelectItem key={obj.id} value={obj.id}>{obj.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsAddingInitiative(false)}>Cancel</Button>
                        <Button
                            onClick={handleAddInitiative}
                            disabled={!newInitiativeData.title.trim() || !newInitiativeData.objectiveId}
                        >
                            Create Initiative
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* GD-7: Edit Objective Dialog */}
            <Dialog open={!!editingObjective} onOpenChange={(open) => { if (!open) setEditingObjective(null); }}>
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader><DialogTitle>Edit Objective</DialogTitle></DialogHeader>
                    {editingObjective && (
                        <div className="space-y-4">
                            <div>
                                <Label>Objective Title</Label>
                                <Input
                                    value={editingObjective.title}
                                    onChange={e => setEditingObjective({ ...editingObjective, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Key Result</Label>
                                <Input
                                    value={editingObjective.keyResult}
                                    onChange={e => setEditingObjective({ ...editingObjective, keyResult: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Quarter</Label>
                                    <Select
                                        value={editingObjective.targetQuarter}
                                        onValueChange={(v) => setEditingObjective({ ...editingObjective, targetQuarter: v as Quarter })}
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Q1">Q1</SelectItem>
                                            <SelectItem value="Q2">Q2</SelectItem>
                                            <SelectItem value="Q3">Q3</SelectItem>
                                            <SelectItem value="Q4">Q4</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Year</Label>
                                    <Input
                                        type="number"
                                        value={editingObjective.targetYear}
                                        onChange={e => setEditingObjective({ ...editingObjective, targetYear: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditingObjective(null)}>Cancel</Button>
                        <Button
                            onClick={handleSaveEditObjective}
                            disabled={!editingObjective?.title.trim()}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* GD-6: Delete Confirmation AlertDialog */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete {deleteTarget?.type} &quot;{deleteTarget?.title}&quot;?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteTarget(null)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default GoalDrivenRoadmap;
